import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Input, Textarea } from '@/components/ui/Input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/Dialog';
import { ErrorState } from '@/components/ui/States';
import { useAuth } from '@/hooks/useAuth';
import { useCollectSample, useReceiveSample, useRejectSample, useRecollectSample } from './hooks';
import { REJECTION_REASONS, Barcode } from './ui';
import { fetchSampleLabel, type SampleLabel } from './api';
import type { LabSample, RejectionReason } from './types';
import { DispatchButton } from './OutsideLabItemDialog';

/**
 * The four things that can happen to a tube — collect, receive, reject,
 * recollect — as one row of buttons, shown wherever a sample is.
 *
 * Which buttons appear is driven by the sample's CURRENT status, mirroring
 * backend/utils/labStatus.js's transition table. That mirroring is a
 * convenience only: the server re-checks every transition and returns 422 for
 * an illegal one, so a stale screen offering the wrong button is refused
 * rather than accepted. Hiding a button is never the control.
 */
export interface SampleActionsProps {
  sample: LabSample;
  /** Compact layout for a dense queue row. */
  compact?: boolean;
}

export default function SampleActions({ sample, compact = false }: SampleActionsProps) {
  const { user } = useAuth();
  const collect = useCollectSample();
  const receive = useReceiveSample();
  const reject = useRejectSample();
  const recollect = useRecollectSample();

  const [error, setError] = useState<string | null>(null);
  const [collectOpen, setCollectOpen] = useState(false);
  const [location, setLocation] = useState('');
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState<RejectionReason>('hemolysed');
  const [remarks, setRemarks] = useState('');
  const [label, setLabel] = useState<SampleLabel | null>(null);

  const orderId = sample.lab_order_id ?? sample.order_id;
  const canCollect = !!user?.modules?.includes('lab:collect');
  // An outsourced tube is dispatched, never received at this bench — the
  // server refuses it (routes/laboratory/samples.js); this just offers the
  // right button instead.
  const outsourced = sample.processing_type === 'outsourced';
  const canReceive = !!user?.modules?.includes('lab:receive') && !outsourced;
  const canRejectHere = !!user?.modules?.includes('lab:receive');
  const canOutsource = !!user?.modules?.includes('lab:outsource');
  const busy = collect.isPending || receive.isPending || reject.isPending || recollect.isPending;

  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    try { await fn(); } catch (e) { setError((e as Error).message); }
  };

  const openLabel = async () => {
    setError(null);
    try { setLabel(await fetchSampleLabel(sample.id)); }
    catch (e) { setError((e as Error).message); }
  };

  const size = compact ? 'sm' : 'sm';

  return (
    <div className={compact ? 'flex flex-wrap items-center gap-1.5' : 'space-y-2'}>
      <div className="flex flex-wrap items-center gap-1.5">
        {sample.status === 'PENDING_COLLECTION' && canCollect && (
          <Button size={size} disabled={busy} onClick={() => setCollectOpen(true)}>Collect</Button>
        )}

        {sample.status === 'COLLECTED' && canReceive && (
          <>
            <Button size={size} disabled={busy}
              onClick={() => run(() => receive.mutateAsync({ id: sample.id, orderId, accept: true }))}>
              Receive &amp; accept
            </Button>
            <Button variant="ghost" size={size} disabled={busy}
              onClick={() => run(() => receive.mutateAsync({ id: sample.id, orderId, accept: false }))}>
              Receive only
            </Button>
          </>
        )}

        {sample.status === 'RECEIVED' && canReceive && (
          <Button size={size} disabled={busy}
            onClick={() => run(() => receive.mutateAsync({ id: sample.id, orderId, accept: true }))}>
            Accept
          </Button>
        )}

        {outsourced && sample.status === 'COLLECTED' && canOutsource && (
          <DispatchButton sampleId={sample.id} referenceLabName={sample.reference_lab_name} orderId={orderId} onError={setError} />
        )}
        {outsourced && (
          <span className="text-[11px] text-muted-foreground">
            Outside lab{sample.reference_lab_name ? ` · ${sample.reference_lab_name}` : ''}
            {sample.external_accession_number ? ` · ${sample.external_accession_number}` : ''}
          </span>
        )}

        {['COLLECTED', 'RECEIVED', 'ACCEPTED', 'PROCESSING', 'SENT_TO_REFERENCE_LAB'].includes(sample.status) && canRejectHere && (
          <Button variant="danger" size={size} disabled={busy} onClick={() => setRejectOpen(true)}>Reject</Button>
        )}

        {['REJECTED', 'RECOLLECTION_REQUIRED'].includes(sample.status) && canCollect && !sample.recollection_of_id && (
          <Button size={size} disabled={busy}
            onClick={() => run(() => recollect.mutateAsync({ id: sample.id, orderId }))}>
            Raise recollection
          </Button>
        )}

        {sample.barcode && (
          <Button variant="ghost" size={size} onClick={openLabel}>Print barcode</Button>
        )}
      </div>

      {error && <ErrorState message={error} />}

      {/* ── Collect ─────────────────────────────────────────────── */}
      <Dialog open={collectOpen} onOpenChange={(o) => { if (!o) setCollectOpen(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Record collection</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <p className="text-[12px] text-muted-foreground">
              A barcode is generated for this tube when you confirm — it names this physical sample and is
              never reused, so a rejected tube can never scan as its replacement.
            </p>
            <Input
              placeholder="Collection location (e.g. OP Collection Room, Ward 3)"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCollectOpen(false)}>Cancel</Button>
            <Button
              disabled={collect.isPending}
              onClick={async () => {
                await run(async () => {
                  const created = await collect.mutateAsync({ id: sample.id, orderId, collection_location: location.trim() || undefined });
                  setCollectOpen(false);
                  setLocation('');
                  // Straight to the label: a collected tube without a printed
                  // barcode on it is the failure this whole step exists to
                  // prevent, so the print sheet opens itself.
                  if (created.barcode) await openLabel();
                });
              }}
            >
              {collect.isPending ? 'Recording…' : 'Confirm collection'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Reject ──────────────────────────────────────────────── */}
      <Dialog open={rejectOpen} onOpenChange={(o) => { if (!o) setRejectOpen(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Reject this sample</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <select
              className="h-9 w-full rounded-lg border border-border bg-card px-2 text-[13px]"
              value={reason}
              onChange={(e) => setReason(e.target.value as RejectionReason)}
            >
              {REJECTION_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <Textarea
              rows={2}
              placeholder={reason === 'other' ? 'Remarks (required for “other”)' : 'Remarks (optional)'}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
            />
            <p className="text-[11px] text-muted-foreground">
              The tests on this tube move to “recollection needed” against the same order — the patient is
              not billed again, and the doctor is still owed the result.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectOpen(false)}>Cancel</Button>
            <Button
              variant="danger"
              disabled={reject.isPending || (reason === 'other' && !remarks.trim())}
              onClick={() => run(async () => {
                await reject.mutateAsync({ id: sample.id, orderId, reason, remarks: remarks.trim() || undefined });
                setRejectOpen(false); setRemarks('');
              })}
            >
              {reject.isPending ? 'Rejecting…' : 'Reject sample'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Printable label ─────────────────────────────────────── */}
      <Dialog open={label !== null} onOpenChange={(o) => { if (!o) setLabel(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Sample label</DialogTitle></DialogHeader>
          {label && (
            <div id="lab-sample-label" className="rounded-lg border border-border bg-white p-3 text-black">
              <div className="text-[12px] font-semibold">{label.patient_name}</div>
              <div className="text-[11px]">{label.uhid} · {label.order_number}</div>
              <div className="text-[11px]">{[label.sample_type, label.container].filter(Boolean).join(' · ')}</div>
              <div className="text-[11px]">{label.tests.join(', ')}</div>
              <div className="mt-2 flex justify-center"><Barcode value={label.barcode} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setLabel(null)}>Close</Button>
            {/*
              window.print() prints the page, which is the pragmatic option
              without a dedicated label-printer integration: the browser's own
              print dialog lets staff pick the label printer and the paper
              size their hospital actually uses.
            */}
            <Button onClick={() => window.print()}>Print</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
